import { Component } from 'react';

import { MainProps, MainState } from 'components/Main';
import { DEFAULT_SEARCH_LAYOUT } from 'data/static';
import { saveAs } from 'file-saver';
import { lastUpdate, systemInfo } from 'services/system';
import { tagList } from 'services/tags';
import {
    transistFullDataTransist,
    transitDownloadSweptPath,
    transitExport,
    transitExportAll,
    transitGetSweptPath,
} from 'services/transits';
import { vesselCategoriesList } from 'services/vesselCategories';
import { TransitType } from 'types';
import {
    ExpandTransitInterface,
    GetBestPositionInterface,
    GetDisplayedTransitsInterface,
    GetOptionInterface,
    GetSelectedTranistInterface,
    GetTransitInterface,
    InspectTransitInterface,
    MainUpdateTransitsInterface,
    RefreshTaglistInterface,
    setTransitsInterface,
    TransitDownloadSweptPathFileInterface,
    TransitExportFileInterface,
    UpdateDisplayAllInterface,
    UpdateTransitOptionFromArrayInterface,
    UpdateTransitOptionInterface,
    UpdateTransitOptionsInterface,
} from 'types/functions';
import { TransitTag } from 'types/system';
import { findNearestPoint } from 'utils/drawing';
import { downloadFile } from 'utils/files';
import { arrayToIndexedObject, clone, getId } from 'utils/formHandlers';
import heartbeat from 'utils/heartBeat';
import Transits from 'utils/transits';
import { translate } from 'utils/translate';

import { portRecord } from 'services/ports';
import { savedScreensRecord } from 'services/savedScreens';
import { FilterObjectType } from 'types/search';
import { userTypeForPort } from 'utils/permission';
import { SETTINGS } from '../../settings';

const AFTER_INSPECT_TOOLS = ['transitDownloadSweptPathFile', 'editTransit', 'downloadTransit'];

export class MainBase extends Component<MainProps, MainState> {
    private isDownloading = false;

    updateSelection: UpdateTransitOptionInterface = async (id, value) => {
        //BACK 2.1
        //const transitClass = new Transits(this.setTransits, this.state.layout.searchId);
        //REMOVE 2.1
        const transitClass = new Transits(this.setTransits, this.props.layout.searchId);
        await transitClass.select(this.state.transits, id, value);
    };

    updateSelectionFromArray: UpdateTransitOptionFromArrayInterface = async (ids, values) => {
        //BACK 2.1
        //const transitClass = new Transits(this.setTransits, this.state.layout.searchId);
        //REMOVE 2.1
        const transitClass = new Transits(this.setTransits, this.state.layout.searchId);
        await transitClass.selectFromArray(this.state.transits, ids, values);
    };

    updatehighlight: UpdateTransitOptionInterface = async (id, value, hide = true) => {
        const transitClass = new Transits(this.setTransits, this.props.layout.searchId);
        if (hide) {
            this.props.mapControls.chart?.hideTooltips();
        }
        await transitClass.highlight(this.state.transits, id, value);
    };

    updateDisplay: UpdateTransitOptionInterface = async (id, value, hide = true) => {
        const transitClass = new Transits(this.setTransits, getId());
        if (hide) {
            this.props.mapControls.chart?.hideTooltips();
        }
        await transitClass.display(this.state.transits, id, value);
    };

    updateHighlightAll: UpdateTransitOptionsInterface = async (ids, value, hide = true) => {
        const transitClass = new Transits(this.setTransits, this.state.layout.searchId);
        if (hide) {
            this.props.mapControls.chart?.hideTooltips();
        }
        await transitClass.highlightAll(this.state.transits, ids, value);
    };

    getDisplayedTransits: GetDisplayedTransitsInterface = () => {
        return this.state.transits
            .filter((transit: TransitType) => {
                return transit.options.display;
            })
            .map((transit: TransitType) => {
                return transit.id;
            });
    };

    updateDisplayAll: UpdateDisplayAllInterface = async (ids, value, hide = true) => {
        console.log('updateDisplayAll');
        const transitClass = new Transits(this.setTransits, getId());
        if (hide) {
            this.props.mapControls.chart?.hideTooltips();
        }

        await transitClass.displayAll(this.state.transits, ids, value);
    };

    updateDrawSweptPath: UpdateTransitOptionInterface = async (id, value) => {
        const transitClass = new Transits(this.setTransits, getId());
        if (id === 0) {
            await transitClass.sweptPathAll(this.state.transits, value);
            return;
        }
        await transitClass.sweptPath(this.state.transits, id, value);
    };

    updateDrawTrack: UpdateTransitOptionInterface = async (id, value) => {
        const transitClass = new Transits(this.setTransits, getId());
        if (id === 0) {
            await transitClass.trackAll(this.state.transits, value);
            return;
        }
        await transitClass.track(this.state.transits, id, value);
    };

    updateDrawVesselOutline: UpdateTransitOptionInterface = async (id, value) => {
        const transitClass = new Transits(this.setTransits, getId());
        if (id === 0) {
            await transitClass.outlineAll(this.state.transits, value);
            return;
        }
        await transitClass.outline(this.state.transits, id, value);
    };

    transitExportFile: TransitExportFileInterface = (transit) => {
        if (!transit) {
            window?.log('ERROR : Invalid transit data', 'colour: red');
            return;
        }

        this.props.showMessageAction({
            messageType: 'INFO',
            message: translate(
                "Preparing your file. Please wait your download will start automatically once it's ready."
            ),
        });

        transitExport(this.props.port.id, transit.id)
            .then((res) => {
                const filename = `TA transit ${transit.id} - ${
                    transit.vessel.name
                } start ${transit?.start_utc.formatDateTimeToString('dd LLL yyyy hh:mm:ss')}`;
                const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
                saveAs(blob, `${filename}.xlsx`);
            })
            .catch((errors) => {
                this.props.setErrorHandlerAction(errors);
            });
    };

    transitsExportFile = () => {
        if (
            !this.state.transits.filter((x) => {
                return x.options.select;
            }).length
        ) {
            this.props.showMessageAction({
                messageType: 'WARN',
                message: translate('No transits selected.'),
            });

            return false;
        }

        this.props.showMessageAction({
            messageType: 'INFO',
            message: translate(
                "Preparing your file. Please wait your download will start automatically once it's ready."
            ),
        });

        this.transitExportAllTry(
            this.state.transits
                .filter((x) => {
                    return x.options.select;
                })
                .map((x) => {
                    return x.id;
                }),
            getId(),
            0
        );
    };

    transitExportAllTry = (transits, id, retry) => {
        transitExportAll(this.props.port.id, transits, id, retry)
            .then((res) => {
                downloadFile(
                    `/static/tmp/${id}.xlsx`,
                    `TA search result ${transits.length} transits at ${new Date().toISOString().substring(0, 16)}Z.xlsx`
                );
            })
            .catch((error) => {
                if (retry < 5 && error?.response?.status === 504) {
                    if (retry === 1) {
                        this.props.showMessageAction({
                            messageType: 'INFO',
                            message: translate('Preparing data will take some time please wait.'),
                        });
                    }
                    this.transitExportAllTry(transits, id, retry + 1);
                } else {
                    if (error?.response?.status === 504) {
                        this.props.showMessageAction({
                            messageType: 'ERROR',
                            message: translate("Couldn't download this data file"),
                        });
                    } else {
                        this.props.setErrorHandlerAction(error);
                    }
                }
            });
    };

    transitDownloadSweptPathFile: TransitDownloadSweptPathFileInterface = (id) => {
        if (!id) {
            window?.log('ERROR : Invalid transit data', 'colour: red');
            return;
        }

        if (this.isDownloading) {
            return;
        }

        this.isDownloading = true;

        this.props.showMessageAction({
            messageType: 'INFO',
            message: translate(
                "Preparing your file. Please wait your download will start automatically once it's ready."
            ),
        });

        transitDownloadSweptPath(this.props.port.id, id)
            .then((res) => {
                const filename = `TA swept path ${id}`;
                const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });

                saveAs(blob, `${filename}.json`);
            })
            .catch((errors) => {
                this.props.setErrorHandlerAction(errors);
            })
            .finally(() => {
                // ⏳ Unlock after 5 seconds
                setTimeout(() => {
                    this.isDownloading = false;
                }, 5000);
            });
    };

    getBestPosition: GetBestPositionInterface = (id, positionId, target, dataType) => {
        const { transit, transitData } = this.getTransit(id);
        const data = transitData?.[dataType || transit.best];
        if (positionId) {
            return data.find((x) => {
                return x.id === positionId;
            });
        }
        return findNearestPoint(data, target);
    };

    //@ts-ignore
    getTransit: GetTransitInterface = (id: number | string | null) => {
        if (id === 0) {
            return { transits: this.state.transits, transitsData: this.state.transitsData }; // Explicitly return an array
        }

        if (typeof id === 'number' || typeof id === 'string') {
            const transit = this.state.transits.find((x) => x.id === id);
            if (!transit) {
                return { transit: undefined, transitData: undefined }; // Handle null or other unexpected cases
            }
            //@ts-ignore
            return { transit: transit, transitData: this.state.transitsData[transit?.uuid] }; // Return a single TransitType or undefined
        }

        return { transit: undefined, transitData: undefined }; // Handle null or other unexpected cases
    };

    updateTransits: MainUpdateTransitsInterface = async (
        transitIds,
        updates,
        updateSweptPath = false,
        offset: null
    ) => {
        try {
            const res = await transistFullDataTransist(this.props.port.id, this.props.mode,updates, transitIds);

            const sweptPathToLoad: number[] = [];
 
            this.state.transits
                .filter((transit: TransitType) => {
                    return transitIds.includes(transit.id);
                })
                .forEach((transit: TransitType) => {
                    sweptPathToLoad.push(transit.id);
                });
            let sweptPaths: any = [];

            if (updateSweptPath) {
                const sweptPathResults = await this.transitGetSweptPathData(sweptPathToLoad );
                sweptPaths = sweptPathResults.sweptPaths;
            }
            let indexedTransits =  arrayToIndexedObject(res.items);

            let updatedTransits: any[] = [];
            let updatedTransitsData: any = [];

            this.setState(
                (prevState) => {
                    const { transits: currentTransits, transitsData: currenttransitsData } = prevState;

                    const dateColumns = ['updated_at', 'start_utc', 'end_utc', 'created_at'];

                    updatedTransitsData = currenttransitsData;

                    updatedTransits = currentTransits.map((transit) => {
                        const updatedTransit = indexedTransits[transit.id];

                        if (!updatedTransit) {
                            return transit;
                        }

                        // Update swept path data
                        if (updateSweptPath) {
                            const associatedSweptPaths = sweptPaths.filter(
                                (path: any) => path.transit_id === transit.id
                            );
                            const newOffsetId = getId();
                            updatedTransitsData[transit.uuid] = {
                                ...updatedTransitsData[transit.uuid],
                                //@ts-ignore
                                ...(offset && {
                                    offsets: {
                                        ...updatedTransitsData[transit.uuid].offsets,
                                        [newOffsetId]: offset,
                                    },
                                }),

                                ...(updates.includes('smoothed') &&
                                    updatedTransit.smoothed_positions && {
                                        smoothed: updatedTransit.smoothed_positions.map((z: any) => ({
                                            ...z,
                                            vessel_offset_id: newOffsetId,
                                        })),
                                        smoothedId: getId(),
                                    }),

                                ...(updates.includes('centroid') &&
                                    updatedTransit.positions && {
                                        centroid: updatedTransit.positions.map((z: any) => ({
                                            ...z,
                                            vessel_offset_id: newOffsetId,
                                        })),
                                        centroidId: getId(),
                                    }),

                                sweptPath: [...associatedSweptPaths],
                                sweptPathId: getId(),
                            };
                        }

                        // Build updated transit object
                        const updatedFields = {
                            ...transit 
                        };

                        updates.forEach((update) => {
                            updatedFields[update] = dateColumns.includes(update)
                                ? this.props.dateTimeFormat.parseDateTime(updatedTransit[update])
                                : updatedTransit[update];
                        });

                        return updatedFields;
                    });

                    return {
                        transits: updatedTransits,
                        transitsData: updatedTransitsData,
                    };
                },
                () => {
                    if (updates.includes('inspect')) {
                        this.props.setLayoutAction([
                            {
                                action: 'DEFAULT',
                                data: {
                                    inspectId: getId(),
                                    //  searchId: getId(),
                                    inspectTransit: this.props.layout.inspectTransit,
                                    updateTransit: null,
                                    inspectSelectedTime: this.props.layout.inspectSelectedTime,
                                },
                            },
                        ]);
                    }
                    if (updateSweptPath) {
                        this.props.mapControls.chart?.updateTransits(updatedTransits, updatedTransitsData, [
                            'display',
                            'select',
                            'inspect',
                            'drawTrack',
                            'outline',
                            'drawSweptPath',
                        ]);
                    }
                }
            );
        } catch (errors) {
            console.log(errors);
            this.props.setErrorHandlerAction(errors);
        }
    };

    refreshTaglist: RefreshTaglistInterface = async () => {
        const { tags, systemTagsStatus } = await this.getPortTags();
        //@ts-ignore

        if (!systemTagsStatus) {
            // if any errors while loading system will stop
            window.log('ERROR : system failed to refresh tag list', 'colour: red');
            this.props.showSystemErrorAction();
            return false;
        }
        // set tags to store
        this.props.setTagsAction(tags);
    };

    getSystemUpdate = () => {
        //@ts-ignore
        return heartbeat(async () => {
            const { system, systemUpdateStatus } = await this.lastUpdateData();
            if (!systemUpdateStatus) {
                // if any errors while loading system will stop loading
                window.log('ERROR : system failed to get system update', 'colour: red');
                if (this.props.system?.networkError === false) {
                    this.props.showMessageAction({
                        messageType: 'ERROR',
                        message: translate('Oops! Looks like  there is a network issue. Please check your connection'),
                    });
                }
                this.props.setSystemAction({ ...this.props.system, networkError: true }, false);
                return false;
            }

            this.checkSystemVersion(system, false);
        }, 60000);
    };

    checkSystemVersion = (system, firstCheck) => {
        const storeSystem = JSON.parse(localStorage.getItem('system') || '{}');

        if (
            this.state.systemUpdateCheck.state &&
            storeSystem?.version &&
            system &&
            (system?.system.version !== storeSystem?.version ||
                system?.system.date !== storeSystem?.date ||
                system?.system.build !== storeSystem?.build)
        ) {
            this.props.openModelFunction('systemUpdatedModel', 1000, 'permanent', system, {
                onCancel: () => {
                    this.state.systemUpdateCheck.cancel();
                    this.setState({
                        systemUpdateCheck: {
                            state: false,
                            cancel: this.getSystemUpdate(),
                        },
                    });
                    this.props.closeModelFunction('systemUpdatedModel');
                },
            });
        } else {
            // set system to store
            this.props.setSystemAction({ ...system, networkError: false }, this.state.systemUpdateCheck.state);
            if (firstCheck) {
                this.setState({
                    systemUpdateCheck: {
                        state: true,
                        cancel: this.getSystemUpdate(),
                    },
                });
            }
        }
    };

    getDefaultData = () => {
        heartbeat(async () => {
            const { defaultData, systemInfoStatus } = await this.systemInfoData();
            if (!systemInfoStatus) {
                // if any errors while loading system will stop loading
                window.log('ERROR : system failed to get info data', 'colour: red');
                this.props.showSystemErrorAction();
                return false;
            }
            // set defaultData to store
            this.props.setDefaultDataAction(defaultData);
        }, 600000);
    };

    getSavedScreensRecord = async (uuid: string) => {
        try {
            const data = await savedScreensRecord(uuid);
            return { savedData: data.items, savedDataStatus: true };
        } catch (err) {
            if (err.status === 401) {
                //@ts-ignore
                window.location = `${SETTINGS.BASE_URL}home`;
            }
            return { savedData: {}, savedDataStatus: false };
        }
    };

    getPortTags = async () => {
        try {
            //@ts-ignore
            const tagsList = await tagList(localStorage.getItem('port_id'));
            return { tags: tagsList.items, systemTagsStatus: true };
        } catch (err) {
            if (err.status === 401) {
                //@ts-ignore
                window.location = `${SETTINGS.BASE_URL}home`;
            }
            return { tags: [], systemTagsStatus: false };
        }
    };

    systemInfoData = async () => {
        try {
            //@ts-ignore
            const systemData = await systemInfo(localStorage.getItem('port_id'));
            return {
                //@ts-ignore
                defaultData: systemData.defaultData[localStorage.getItem('port_id')],
                system: systemData.system,
                systemInfoStatus: true,
            };
        } catch (err) {
            if (err.status === 401) {
                //@ts-ignore
                window.location = `${SETTINGS.BASE_URL}home`;
            }
            return { defaultData: {}, system: {}, systemInfoStatus: false };
        }
    };

    getPortData = async () => {
        try {
            const portData = await portRecord(localStorage.getItem('port_id'));
            return { portData: portData.items, getPortDataStatus: true };
        } catch (err) {
            if (err.status === 401) {
                //@ts-ignore
                window.location = `${SETTINGS.BASE_URL}home`;
            }
            return { portData: {}, getPortDataStatus: false };
        }
    };

    lastUpdateData = async () => {
        console.log(this.props.dateTimeFormat);
        try {
            const systemData = await lastUpdate(this.props.port.id);
            return {
                system: {
                    system: systemData.system,
                    date: {
                        today: this.props.dateTimeFormat.parseDateTime(systemData.today, 'Server today'),
                        endDate: this.props.dateTimeFormat.parseDateTime(systemData.endDate, 'System start date'),
                        startDate: this.props.dateTimeFormat
                            .parseDateTime(systemData.startDate, 'System end date')
                            .startOf('day'),
                    },
                    update: {
                        messages: {
                            ...systemData.items,
                            valid_time_utc: this.props.dateTimeFormat.parseDateTime(
                                systemData.items.valid_time_utc,
                                'last AIS message'
                            ),
                        },
                        environmental: {
                            ...systemData.environmental,
                            valid_time_utc: this.props.dateTimeFormat.parseDateTime(
                                systemData.environmental.valid_time_utc,
                                'last environmental message'
                            ),
                        },
                    },
                    transits: {
                        forReview: systemData.forReview,
                        simulated: systemData.simulated,
                        total: systemData.total,
                        ppu: systemData.ppu,
                    },
                },
                systemUpdateStatus: true,
            };
        } catch (err) {
            if (err.status === 401) {
                //@ts-ignore
                window.location = `${SETTINGS.BASE_URL}home`;
            }
            return { system: {}, systemUpdateStatus: false };
        }
    };

    getSystemVesselCategories = async () => {
        try {
            const tagsList = await vesselCategoriesList();
            return { vesselCategories: tagsList.items, vesselCategoriesStatus: true };
        } catch (err) {
            return { vesselCategories: [], vesselCategoriesStatus: false };
        }
    };

    expandTransit: ExpandTransitInterface = async (id) => {
        const transitClass = new Transits(this.setTransits, this.state.layout.searchId);

        await transitClass.expand(this.state.transits, id);
    };

    inspectTransit: InspectTransitInterface = async (id, time = null) => {
        const userType = userTypeForPort(this.props.user, this.props.port.id);
        if (id === this.state.layout.inspectTransit) {
            this.props.setLayoutAction([
                {
                    action: 'DEFAULT',
                    data: {
                        inspectId: getId(),
                        inspectTransit: id,
                        //searchId: getId(),
                        inspectSelectedTime: time,
                    },
                },
                id !== 0 && {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'chart',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: true,
                    },
                },
                id !== 0 && {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'grid',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: true,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'chart',
                        state: true,
                        active: true,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'grid',
                        state: true,
                        active: true,
                    },
                },
            ]);

            return;
        }

        const transitClass = new Transits(this.setTransits, this.state.layout.searchId);

        const transits = await transitClass.inspect(this.state.transits, id);

        const isInspected = transits.some((transit) => transit.options?.inspect === true);

        if (!isInspected) {
            this.props.setLayoutAction([
                {
                    action: 'DEFAULT',
                    data: {
                        inspectTransit: id,
                        // searchId: getId(),
                        inspectId: getId(),
                    },
                },
                {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'chart',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: false,
                    },
                },
                {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'grid',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: false,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'chart',
                        state: true,
                        active: true,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'grid',
                        state: true,
                        active: true,
                    },
                },
                // { action: 'UPDATE_SECTION', data: { name: 'chart', state: true, display: true, disable: false } },
            ]);

            return;
        } else {
            this.props.setLayoutAction([
                {
                    action: 'DEFAULT',
                    data: {
                        inspectId: getId(),
                        //  searchId: getId(),
                        inspectTransit: id,
                        inspectSelectedTime: time,
                    },
                },
                {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'chart',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: true,
                    },
                },
                {
                    action: 'UPDATE_TOOLS',
                    data: {
                        section: 'grid',
                        names: AFTER_INSPECT_TOOLS.filter((x) => {
                            if (x === 'editTransit' && userType === 'guest') {
                                return false; // exclude editTransit
                            }
                            return true; // keep everything else
                        }),
                        active: true,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'chart',
                        state: true,
                        display: true,
                    },
                },
                {
                    action: 'UPDATE_MENU',
                    data: {
                        name: 'inspectTransitMenu',
                        section: 'grid',
                        state: true,
                        display: true,
                    },
                },
                // { action: 'UPDATE_SECTION', data: { name: 'chart', state: true, display: true, disable: false } },
            ]);
        }
    };

    getSelectedTranist: GetSelectedTranistInterface = () => {
        return { selectedTransit: this.state.selectedTransit, selectedTime: this.state.selectedTime };
    };

    createSearchObject = (set = true): FilterObjectType => {
        console?.log('EVENT Create search object', 'colour: orange');
        const setEnvironmentalState = (obj: { [key: string]: object }) => {
            Object.keys(obj).forEach((key) => {
                obj[key]['status'] = false;
            });

            return obj;
        };

        const filterObject = {
            version: '2.0.0',
            vesselCategories: [],
            limit: DEFAULT_SEARCH_LAYOUT,
            pilots: [],
            tags: [],
            origin: ['AIS'],
            vessel: {
                name: { icontains: '' },
                loa_m: clone(this.state.defaultData.vessel.loa_m),
                beam_m: clone(this.state.defaultData.vessel.beam_m),
            },
            transits: {
                draft_m: clone(this.state.defaultData.transits.draft_m),
            },
            date: {
                lastDays: '',
                dateRange: '',
                start_utc: {
                    op: null,
                },
                end_utc: {
                    op: null,
                },
            },
            withComments: false,
            watercurrent: clone(setEnvironmentalState(this.state.defaultData.watercurrent)),
            wave: clone(setEnvironmentalState(this.state.defaultData.wave)),
            tide: clone(setEnvironmentalState(this.state.defaultData.tide)),
            wind: clone(setEnvironmentalState(this.state.defaultData.wind)),
        };
        if (set) {
            this.props.setSearchObjectAction(clone(filterObject));
            this.props.setHighlightObjectAction(clone(filterObject));
        }

        return filterObject;
    };

    transitGetSweptPathData = async (ids: number[]) => {
        // If no IDs are provided, return an empty result immediately
        if (!ids || ids.length === 0) {
            return { sweptPaths: [], sweptPathResults: true };
        }

        try {
            const tagsList = await transitGetSweptPath(this.props.port.id, { id: ids });
            return { sweptPaths: tagsList.items, sweptPathResults: true };
        } catch (err) {
            return { sweptPaths: [], sweptPathResults: false };
        }
    };

    setTransits: setTransitsInterface = async (transits, transitData, searchId, update = true, force = false) => {
        console.time('setTransits total');

        // STEP A1: Initial check
        console.time('step A 1: initial check');
        if (transits.length === 0) {
            this.props.mapControls.chart?.updateTransits([], {}, []);
            console.timeEnd('step A 1: initial check');
            console.timeEnd('setTransits total');
            this.setState({ transits: [] });
            return;
        }
        console.timeEnd('step A 1: initial check');

        // STEP A2: Setup
        console.time('step A 2: setup');
        const newSearch = searchId !== this.state.layout.searchId;
        const sweptPathToLoad: number[] = [];

        transits.forEach((transit: TransitType) => {
            if (!transit.options.sweptPathLoaded && this.props.switchs?.['sweptPath'].value) {
                sweptPathToLoad.push(transit.id);
            }
        });
        console.timeEnd('step A 2: setup');

        // STEP A3: Fetch swept paths
        console.time('step A 3: fetch swept paths');
        const { sweptPaths, sweptPathResults } = await this.transitGetSweptPathData(sweptPathToLoad);
        console.timeEnd('step A 3: fetch swept paths');

        if (!sweptPathResults) {
            this.props.showSystemErrorAction();
            console.timeEnd('setTransits total');
            return;
        }

        // STEP A4: Update transits
        console.time('step A 4: update transits');
        const updatedTransitsData = transitData;
        const updatedTransits = transits.map((transit: TransitType) => {
            const associatedSweptPaths = sweptPaths.filter((path: TransitTag) => path.transit_id === transit.id);

            if (sweptPathToLoad.includes(transit.id)) {
                if (associatedSweptPaths.length === 0) {
                    window?.log(`ERROR : Transit ${transit.id} has no sweptpath`, 'colour: red');
                }

                if (updatedTransitsData !== null) {
                    updatedTransitsData[transit.uuid] = {
                        ...updatedTransitsData[transit.uuid],
                        sweptPathId: newSearch ? getId() : updatedTransitsData[transit.uuid]?.sweptPathId,
                        sweptPath: associatedSweptPaths,
                    };
                }

                return {
                    ...transit,
                    options: {
                        ...transit.options,
                        sweptPathLoaded: true,
                    },
                };
            }

            return transit;
        });
        console.timeEnd('step A 4: update transits');

        // STEP A5: Set state and update layout
        console.time('step A 5: setState + layout');
        await new Promise<void>((resolve) => {
            const newState: any = { transits: updatedTransits };

            if (updatedTransitsData !== null) {
                newState.transitsData = updatedTransitsData;
            }

            const updatedKeys = this.getChangedKeys(this.state.transits, updatedTransits);
            console.log(updatedKeys);

            this.setState(newState, () => {
                this.props.setLayoutAction([
                    {
                        action: 'DEFAULT',
                        data: {
                            searchId: searchId,
                            displayId: getId(),
                        },
                    },
                    {
                        action: 'UPDATE_TOOLS',
                        data: {
                            keepMenu: true,
                            section: 'grid',
                            names: ['showAllTransits'],
                            active: !transits.every((transit) => transit.options?.display === true),
                        },
                    },
                    {
                        action: 'UPDATE_TOOLS',
                        data: {
                            keepMenu: true,
                            section: 'chart',
                            names: ['showAllTransits'],
                            active: !transits.every((transit) => transit.options?.display === true),
                        },
                    },
                ]);

                // STEP A6: Update chart
                if (update) {
                    console.time('step A 6: chart update');
                    if (newSearch || force) {
                        this.props.mapControls.chart?.updateTransits(
                            updatedTransits,
                            !updatedTransitsData ? this.state.transitsData : updatedTransitsData,
                            updatedKeys
                        );
                    } else {
                        this.props.mapControls.chart?.refreshFeatures();
                    }
                    console.timeEnd('step A 6: chart update');
                }

                resolve(); // ✅ Resolve after setState and updates
            });
        });
        console.timeEnd('step A 5: setState + layout');

        console.timeEnd('setTransits total');
    };

    getChangedKeys = (list1: any[], list2: any[]): string[] => {
        const changedKeys = new Set<string>();

        const length = Math.max(list1.length, list2.length);

        for (let i = 0; i < length; i++) {
            const opts1 = list1[i]?.options || {};
            const opts2 = list2[i]?.options || {};

            const keys = new Set([...Object.keys(opts1), ...Object.keys(opts2)]);
            for (const key of keys) {
                if (opts1[key] !== opts2[key]) {
                    changedKeys.add(key);
                }
            }
        }

        return [...changedKeys]; // convert Set → Array
    };

    getOption: GetOptionInterface = (id, options) => {
        // Find the transit by ID
        const transit = this.state.transits.find((transit) => {
            return transit.id === id || transit.uuid === id;
        });

        // If the transit doesn't exist, return false
        if (!transit) {
            return false;
        }

        // Check if all specified options are true in the transit.options object
        return options.every((option) => transit.options[option] === true);
    };
}
